import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import ReactPlayer from 'react-player';
import { Result } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import StyledButton from '../../../../components/form-control/styledButton/styledButton';
import useTranslation from '../../../../common/hooks/useTranslation';
import { ReactComponent as ConnectionErrorIcon } from './connection-error.svg';

import styles from './VideoPopup.module.css';

interface Props {
  url?: string;
  /** invoked when the video starts playing */
  onPlay?: () => void;
  /** invoked when 80% of the video has been watched */
  onVideoViewed?: () => void;
  /** invoked when the video has finished playing */
  onVideoCompleted?: () => void;
  onClose: () => void;
  wrapperClassName?: string;
}

type VideoProgress = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

const VideoPopup = (props: Props): React.JSX.Element | null => {
  const [parentNotified, setParentNotified] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [playbackStarted, setPlaybackStarted] = useState(false);
  const playerRef = useRef<HTMLVideoElement>(null);
  const lastTimeRef = useRef<number>(0);
  const t = useTranslation('VideoPopup.Error');

  const handleProgress: React.ReactEventHandler<HTMLVideoElement> = (event) => {
    const video = event.currentTarget;
    const lastTime = lastTimeRef.current;
    const delta = video.currentTime - lastTime;
    const playedTimeSec = lastTime + delta;
    const playedPercent = playedTimeSec / video.duration;

    if (playedPercent > 0.85 && !parentNotified) {
      props.onVideoViewed && props.onVideoViewed();
      setParentNotified(true);
    }
  };

  const handleError = () => {
    setShowFallback(true);
  };

  if (!props.url) {
    return null;
  }
  return (
    <div
      className={classNames(
        styles.Wrapper,
        props.wrapperClassName,
        showFallback && styles.HasError,
      )}
    >
      {showFallback ? (
        <div className={styles.OfflineContainer}>
          <Result
            className={styles.ResultContainer}
            icon={<ConnectionErrorIcon className={styles.Icon} />}
            title={t('Title', 'Network Error')}
            subTitle={t('Subtitle')}
            extra={
              <StyledButton
                type='button'
                color='primary'
                title={t('Button', 'Close')}
                onClick={props.onClose}
              />
            }
          />
          ,
        </div>
      ) : (
        <ReactPlayer
          ref={playerRef}
          src={props.url}
          controls={true}
          playing={playbackStarted}
          light={true}
          playIcon={
            <div className={styles.PlayButton} aria-hidden>
              <CaretRightOutlined className={styles.PlayCaret} />
            </div>
          }
          onClickPreview={() => setPlaybackStarted(true)}
          muted={false}
          volume={0.5}
          onPlay={props.onPlay}
          onError={handleError}
          onTimeUpdate={handleProgress}
          onEnded={props.onVideoCompleted}
          className={styles.VideoIframe}
          width='100%'
          height='100%'
        />
      )}
    </div>
  );
};

export default VideoPopup;
